import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { reviews } from "@/mocks/reviews";
import { recommendedMovies } from "@/mocks/movies";

const navItems = [
  { label: "首页", to: "/" },
  { label: "剧情", to: "/story" },
  { label: "影评", to: "/reviews" },
  { label: "新闻", to: "/news" },
  { label: "关于", to: "/about" },
];

type SearchResult = {
  type: "review" | "movie";
  id: string;
  title: string;
  sub: string;
  to: string;
};

function buildResults(keyword: string): SearchResult[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  const reviewResults: SearchResult[] = reviews
    .filter((r) => r.title.toLowerCase().includes(kw) || r.tags.join(" ").toLowerCase().includes(kw))
    .map((r) => ({ type: "review", id: r.id, title: r.title, sub: `影评 · ${r.author}`, to: `/reviews/detail/${r.id}` }));
  const movieResults: SearchResult[] = recommendedMovies
    .filter((m) => m.title.toLowerCase().includes(kw))
    .map((m) => ({ type: "movie", id: m.id, title: m.title, sub: `影片 · ${m.year}`, to: "/watch" }));
  return [...movieResults, ...reviewResults].slice(0, 6);
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [focused, setFocused] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setFocused(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setFocused(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = buildResults(keyword);

  const goResult = (to: string) => {
    setKeyword("");
    setFocused(false);
    navigate(to);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 border-background-200 backdrop-blur-md"
          : "bg-background-50/80 border-transparent backdrop-blur-sm"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0 group">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500 text-foreground-950">
              <i className="ri-movie-2-fill text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 whitespace-nowrap">
                色戒在线观看
              </span>
              <span className="text-[10px] tracking-[0.3em] text-secondary-500 uppercase mt-1 whitespace-nowrap">
                Cinema Portal
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? "text-accent-500 bg-background-100"
                      : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div ref={searchRef} className="relative hidden md:block">
              <div className="flex items-center gap-2 h-10 px-3 rounded-md bg-background-100 border border-background-200 focus-within:border-primary-500 transition-colors w-52 xl:w-64">
                <i className="ri-search-line text-secondary-500 text-base" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onFocus={() => setFocused(true)}
                  placeholder="搜索影片或影评"
                  className="w-full bg-transparent text-sm text-foreground-950 placeholder:text-secondary-600 outline-none"
                />
              </div>
              {focused && keyword.trim() && (
                <div className="absolute right-0 mt-2 w-80 rounded-lg bg-background-100 border border-background-200 overflow-hidden animate-fade-in">
                  {results.length > 0 ? (
                    results.map((r) => (
                      <button
                        key={`${r.type}-${r.id}`}
                        onClick={() => goResult(r.to)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-200 text-accent-500 shrink-0">
                          <i className={r.type === "review" ? "ri-file-text-line" : "ri-film-line"} />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm text-foreground-950 truncate">{r.title}</span>
                          <span className="block text-xs text-secondary-500 truncate">{r.sub}</span>
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-5 text-sm text-secondary-500 text-center">未找到相关内容</div>
                  )}
                </div>
              )}
            </div>

            <Link
              to="/watch"
              className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-fill text-base" />
              立即观看
            </Link>

            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md bg-background-100 border border-background-200 text-foreground-950 cursor-pointer"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <div className="px-4 py-4 space-y-4">
            <div className="flex items-center gap-2 h-10 px-3 rounded-md bg-background-100 border border-background-200">
              <i className="ri-search-line text-secondary-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影片或影评"
                className="w-full bg-transparent text-sm outline-none placeholder:text-secondary-600"
              />
            </div>
            {keyword.trim() && (
              <div className="rounded-md bg-background-100 border border-background-200 overflow-hidden">
                {results.length > 0 ? (
                  results.map((r) => (
                    <button
                      key={`m-${r.type}-${r.id}`}
                      onClick={() => goResult(r.to)}
                      className="w-full px-4 py-3 text-left text-sm text-foreground-950 hover:bg-background-200 cursor-pointer"
                    >
                      {r.title}
                      <span className="block text-xs text-secondary-500">{r.sub}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-4 text-sm text-secondary-500 text-center">未找到相关内容</div>
                )}
              </div>
            )}
            <nav className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-md text-sm font-medium text-center transition-colors ${
                      isActive ? "bg-primary-500 text-foreground-950" : "bg-background-100 text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}